import React, { useEffect, useRef, useState } from 'react'
import { Button, Spin } from 'antd'
import moment from 'moment'
import { getEvents, errorText } from './api'
import { EVENT_META, checkLabel, fmtDateTime } from './helpers'

const PAGE = 100
const FILTERS = [
  { key: 'all', label: 'All events', params: {} },
  { key: 'individual', label: 'Individual', params: { mode: 'INDIVIDUAL' } },
  { key: 'bulk', label: 'Bulk', params: { mode: 'BULK' } },
  { key: 'query', label: 'Queries', params: { action: 'QUERY_RAISED' } }
]

// Append-only audit log. Fetched when this tab opens and when its filter changes
// (one call per filter; "Load more" pages). Batch details come in the same
// response, so grouping bulk events costs no extra calls.
const AuditLog = ({ refreshKey }) => {
  const [filter, setFilter] = useState('all')
  const [rows, setRows] = useState([])
  const [batches, setBatches] = useState({})
  const [count, setCount] = useState(0)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const seq = useRef(0)

  const load = (offset) => {
    const my = ++seq.current
    setLoading(true); setError(null)
    const f = FILTERS.find(x => x.key === filter)
    getEvents({ ...f.params, limit: PAGE, offset })
      .then(res => {
        if (my !== seq.current) return
        setRows(prev => (offset ? prev.concat(res.data || []) : (res.data || [])))
        setBatches(prev => ({ ...(offset ? prev : {}), ...(res.batches || {}) }))
        setCount(res.count || 0)
      })
      .catch(err => { if (my === seq.current) setError(errorText(err)) })
      .then(() => { if (my === seq.current) setLoading(false) })
  }
  useEffect(() => { load(0) }, [filter, refreshKey]) // eslint-disable-line react-hooks/exhaustive-deps

  const shown = {}
  const body = []
  rows.forEach(e => {
    if (e.mode === 'BULK' && e.batchId && !shown[e.batchId]) {
      shown[e.batchId] = 1
      const b = batches[e.batchId]
      body.push(
        <tr className='batch' key={'b-' + e.batchId}>
          <td>{b ? fmtDateTime(b.at, moment) : ''}</td>
          <td colSpan={2}>Batch <span className='mono'>{e.batchId}</span>{b ? ' · ' + b.voucherCount + ' vouchers' : ''}{b && b.status !== 'DONE' ? ' · ' + b.status.toLowerCase() : ''}</td>
          <td><span className='pill bulk'>Bulk</span></td>
          <td>{b && b.user ? b.user.name : ''}</td>
          <td>{b ? b.remarks : ''}</td>
          <td className='hint'>{b && b.filtersUsed ? describe(b.filtersUsed) : ''}</td>
        </tr>
      )
    }
    const m = EVENT_META[e.action] || { label: e.action }
    body.push(
      <tr key={e._id}>
        <td>{fmtDateTime(e.at, moment)}</td>
        <td><span className={'type ' + e.voucherType}>{e.voucherType}</span> <span className='mono'>{e.voucherNo}</span></td>
        <td>{m.label}</td>
        <td>{e.mode === 'BULK' ? <span className='pill bulk'>Bulk · {e.batchId}</span> : e.mode === 'INDIVIDUAL' ? 'Individual' : <span className='hint'>System</span>}</td>
        <td>{e.user ? e.user.name : <span className='hint'>System</span>}</td>
        <td>{e.remarks || ''}</td>
        <td className='hint'>{(e.action === 'AUDITED' || e.action === 'QUERY_RAISED') ? (Array.from(new Set(e.checksAtTime || [])).map(checkLabel).join(', ') || 'None') : ''}</td>
      </tr>
    )
  })

  return (
    <section className='cockpit'>
      <div className='logbar'>
        <div className='seg' role='group' aria-label='Log filter'>
          {FILTERS.map(f => (
            <button type='button' key={f.key} aria-pressed={filter === f.key} onClick={() => setFilter(f.key)}>{f.label}</button>
          ))}
        </div>
        <span className='hint'>Append-only. Each bulk action is one batch, showing who ran it, the remark and the filters in force.</span>
      </div>
      {error && <div className='errbox' style={{ margin: 14 }}><b>Could not load the audit log.</b><span>{error}</span></div>}
      <div style={{ overflowX: 'auto' }}>
        <table className='log'>
          <thead><tr><th>When</th><th>Voucher</th><th>Action</th><th>Mode</th><th>By</th><th>Remark</th><th>Checks open at the time</th></tr></thead>
          <tbody>{body}</tbody>
        </table>
      </div>
      {!loading && !error && !rows.length && <div className='empty' style={{ margin: 14 }}>No audit events yet.</div>}
      <div className='more'>
        {loading ? <Spin size='small' /> : rows.length < count && <Button size='small' onClick={() => load(rows.length)}>Load more ({count - rows.length} left)</Button>}
      </div>
    </section>
  )
}

// Same wording as the bulk dialog's "Filters in force".
const describe = (f) => {
  const p = [f.type && f.type !== 'ALL' ? f.type : 'PI and SPI']
  p.push(f.from || f.to ? (f.from || '…') + ' to ' + (f.to || '…') : 'All dates')
  if (f.location) p.push('Location filter')
  if (f.search) p.push('Search "' + f.search + '"')
  return p.join(' · ')
}

export default AuditLog
